/**
 * Data slice reducer — holds computed analysis results in memory.
 *
 * SCENARIO-SCOPED RESULTS
 * -----------------------
 * Analyses that runAll produces (aging, rollforward, delinquencies, dilution,
 * loss, yield, aggregateReserve, buyerParent, concentrationLimits, nerb) are
 * stored per-scenario under `results[scenarioId][key]`, with scenarioId "base"
 * for the original project. This lets the user toggle Original ↔ a modified
 * scenario instantly, without a backend round-trip.
 *
 * `flags[scenarioId]` holds a scenario's has* flags. The BASE project's has*
 * flags deliberately live in the PROJECT slice (unchanged) — routing scenario
 * flags here keeps a scenario run from clobbering the base flags or nulling the
 * base project's saved executive report.
 *
 * Non-scenario keys (master, scrub, fraud, rollforwardAnalysis, report, supplier,
 * dateRange, documentTypes, ...) and engine mode stay flat at the top level.
 */
const initialState = {
  useNumba: true, // numba (JIT-compiled) is the default; pandas kept for debugging
  master: null,

  // Scenario dimension.
  activeScenario: "base",
  results: { base: {} }, // results[scenarioId][analysisKey]
  flags: {}, // flags[scenarioId][hasFlag]  (base flags live in the project slice)
  runStatus: null, // { scenarioId, step, index, total, done, error } — a run in flight
};

// -------------------------------------------------------
// -------------------------------------------------------
const reducer = (state = initialState, action) => {
  // -------------------------------------------------------
  switch (action.type) {
    // ---- Scenario-scoped analysis results ----
    case "SET_RESULT": {
      const sid = action.scenarioId || "base";
      return {
        ...state,
        results: {
          ...state.results,
          [sid]: { ...(state.results[sid] || {}), [action.key]: action.value },
        },
      };
    }

    case "SET_ACTIVE_SCENARIO":
      return { ...state, activeScenario: action.scenarioId || "base" };

    case "SET_SCENARIO_FLAG": {
      const sid = action.scenarioId;
      return {
        ...state,
        flags: {
          ...state.flags,
          [sid]: { ...(state.flags[sid] || {}), [action.flag]: true },
        },
      };
    }

    case "SET_SCENARIO_STATUS": {
      // Bulk-set a scenario's has* flags from a backend analysis-status fetch.
      const sid = action.scenarioId;
      return {
        ...state,
        flags: { ...state.flags, [sid]: { ...(state.flags[sid] || {}), ...action.status } },
      };
    }

    case "CLEAR_SCENARIO_RESULTS": {
      const { [action.scenarioId]: _r, ...results } = state.results;
      const { [action.scenarioId]: _f, ...flags } = state.flags;
      return { ...state, results, flags };
    }

    case "SET_RUN_STATUS":
      // Merge so onStep updates don't have to re-send scenarioId each time.
      return { ...state, runStatus: { ...(state.runStatus || {}), ...action.runStatus } };

    case "CLEAR_RUN_STATUS":
      return { ...state, runStatus: null };

    // ---- Non-scoped keys (base project only) ----
    case "SET_MASTER_DATA":
      return { ...state, master: action.master };

    case "SET_SCRUB_DATA":
      return { ...state, scrub: action.scrub };

    case "SET_FRAUD_DATA":
      return { ...state, fraud: action.fraud };

    case "SET_ROLLFORWARD_ANALYSIS":
      return { ...state, rollforwardAnalysis: action.rollforwardAnalysis };

    case "SET_SUPPLIER_DATA":
      return { ...state, supplier: action.supplier };

    case "SET_ROLLOVER_DISCREPANCIES":
      return { ...state, discrepancies: action.discrepancies };

    case "SET_BUYER_AR":
      return { ...state, buyerAr: action.buyerAr };

    case "TOGGLE_NUMBA":
      return { ...state, useNumba: !state.useNumba };

    case "SET_NUMBA":
      return { ...state, useNumba: action.value };

    case "SET_REPORT_DATA":
      return { ...state, report: action.report };

    case "CLEAR_REPORT":
      return { ...state, report: null };

    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.dateRange };

    case "SET_DOCUMENT_TYPES":
      return { ...state, documentTypes: action.documentTypes };

    case "SET_PREDICTED_NORM_MAP":
      return { ...state, predictedNormMap: action.predictedNormMap };

    default:
      return state;
  }
};

export default reducer;
